import { Component, type ErrorInfo, type ReactNode } from 'react';

import { getTraceId } from '@shared/api';
import { logger } from '@shared/lib/logger';
import { errorReporter } from '@shared/lib/observability';

import { ERROR_BOUNDARY_TEXT } from './error-boundary.text';
import { ErrorFallback } from './error-fallback.ui';

export interface ErrorBoundaryFallbackProps {
  error: unknown;
  reset: () => void;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** 어느 경계인지(로그용). 예: `root`, `route:/fund/:id` */
  name: string;
  fallback?: (props: ErrorBoundaryFallbackProps) => ReactNode;
  /** 값이 바뀌면 에러 상태를 푼다(예: 경로) */
  resetKey?: string;
  inline?: boolean;
}

interface ErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

const CLEAN: ErrorBoundaryState = { error: null, hasError: false };

/** 루트에 하나, 라우트(페이지)마다 하나 둔다. 폴백에 추적 ID 와 다시 시도 버튼이 있다. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = CLEAN;

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error, hasError: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    const traceId = getTraceId(error);
    const context = {
      boundary: this.props.name,
      ...(traceId ? { traceId } : {}),
    };
    logger.error(ERROR_BOUNDARY_TEXT.logRender, {
      ...context,
      error,
      componentStack: info.componentStack ?? '',
    });
    errorReporter.capture(error, context);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps) {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => {
    this.setState(CLEAN);
  };

  override render() {
    if (!this.state.hasError) return this.props.children;
    const props: ErrorBoundaryFallbackProps = { error: this.state.error, reset: this.reset };
    return this.props.fallback ? (
      this.props.fallback(props)
    ) : (
      <ErrorFallback {...props} inline={this.props.inline ?? false} />
    );
  }
}
